import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, Calendar, Users } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { motion, AnimatePresence } from "framer-motion";

/* Animated Counter Component */
const AnimatedCounter = ({ target, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Easing function for smooth deceleration
            const easeOut = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(easeOut * target));
            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [target, duration, hasAnimated]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
};

/* Hero background image - HD 4K modern glass skyscraper */
const HERO_BG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/kv2bcnwz_154baac2-a921-4354-9fbe-ce2c005e53ed.png";

const PROJECT_IMAGES = [
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/4zuiwd6e_ROSELLA%20APARTMENTS.jpg",
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/3l91eguz_633%20Intermain.jpg",
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/gc22lsmj_65efa80f50b6ef0921ae1c12_62687fc159ef4b6633ce2c7b_William_Angliss-47_WEB.jpeg",
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/b36dgykf_castlerock%20nepean%20hwy.webp",
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/r0ho9ps7_Monash%20Uni.jpg",
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/d6z8zxzr_WestUrban.jpg",
  "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/mwtxu36r_aus-vic-port-melbourne-ie-005_high_2000x650.avif",
];

const PROJECTS = [
  { 
    id: 1,
    title: "Rosella Apartments", 
    type: "Builder's Cleaning", 
    category: "builders", 
    value: "Multi-Storey Residential", 
    client: "Markscon", 
    builder: "Markscon",
    completionDate: "2025",
    image: PROJECT_IMAGES[0],
    description: "A comprehensive builder's cleaning for this stunning multi-storey residential development. Our team delivered meticulous cleaning services, ensuring every apartment was handover-ready.",
    scope: [
      "Full internal cleaning of all residential units",
      "Common area and lobby detailing",
      "Window cleaning - internal and external",
      "Balcony and facade cleaning",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Multi-storey residential complex",
    projectDetails: {
      location: "Melbourne, VIC",
      duration: "4 weeks",
      teamSize: "12 cleaners",
      sqMetres: "8,500 m²"
    },
    outcome: "Delivered on time with zero defects reported at handover. All 48 apartments passed first inspection, enabling the developer to meet settlement deadlines. Client reported a 100% satisfaction rate from new residents."
  },
  { 
    id: 2,
    title: "633 Springvale Road", 
    type: "Office Fitout Cleaning", 
    category: "builders", 
    value: "Commercial Office Complex", 
    client: "Intermain", 
    builder: "Intermain",
    completionDate: "2026",
    image: PROJECT_IMAGES[1],
    description: "A comprehensive office fitout cleaning for this modern commercial complex in Springvale. Our team delivered detailed cleaning across multiple levels, ensuring all office spaces were ready for tenant occupation.",
    scope: [
      "Full internal cleaning of all office floors",
      "Common area and lobby detailing",
      "Window cleaning - internal and external",
      "Floor polishing and treatment",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Multi-level commercial office complex",
    projectDetails: {
      location: "Springvale, VIC",
      duration: "3 weeks",
      teamSize: "8 cleaners",
      sqMetres: "6,200 m²"
    },
    outcome: "Completed 2 days ahead of schedule, allowing early tenant fit-out access. The building achieved practical completion certification on first submission, with all cleaning standards exceeding contractual requirements."
  },
  { 
    id: 3,
    title: "William Angliss Institute", 
    type: "Fit Out & Commercial Kitchen Cleaning", 
    category: "builders", 
    value: "Educational Facility", 
    client: "Renascent", 
    builder: "Renascent",
    completionDate: "2026",
    image: PROJECT_IMAGES[2],
    description: "A detailed fitout cleaning for the William Angliss Institute's modern training facilities. Our team delivered meticulous cleaning of specialist kitchen, hospitality and training spaces, ensuring all areas were ready for student use.",
    scope: [
      "Commercial kitchen deep cleaning",
      "Training facility cleaning",
      "Common area and lobby detailing",
      "Window cleaning - internal and external",
      "Floor treatment and polishing",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Educational training facility",
    projectDetails: {
      location: "Melbourne CBD, VIC",
      duration: "2 weeks",
      teamSize: "10 cleaners",
      sqMetres: "4,800 m²"
    },
    outcome: "All commercial kitchens passed health and safety inspections on first assessment. Specialist food-grade cleaning protocols ensured compliance with hospitality industry standards. Facility opened on schedule for the new academic term."
  },
  { 
    id: 4,
    title: "383 Nepean Hwy", 
    type: "Builder's Cleaning", 
    category: "builders", 
    value: "Commercial Development", 
    client: "Castlerock", 
    builder: "Castlerock",
    completionDate: "2026",
    image: PROJECT_IMAGES[3],
    description: "A comprehensive builder's cleaning for this striking commercial development on Nepean Highway. Our team delivered full cleaning services across all levels, ensuring the building was handover-ready to the highest standards.",
    scope: [
      "Full internal cleaning of all floors",
      "Common area and lobby detailing",
      "Window cleaning - internal and external",
      "Facade and external area cleaning",
      "Floor treatment and polishing",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Multi-level commercial development",
    projectDetails: {
      location: "Nepean Highway, VIC",
      duration: "3 weeks",
      teamSize: "9 cleaners",
      sqMetres: "5,400 m²"
    },
    outcome: "Achieved handover certification with zero remedial cleaning required. External facade cleaning completed using rope access techniques, delivering a pristine street presence for the new development."
  },
  { 
    id: 5,
    title: "Monash University Clayton Campus", 
    type: "Deep Cleaning & Sanitization", 
    category: "industrial", 
    value: "Educational Institution", 
    client: "Intermain", 
    builder: "Intermain",
    completionDate: "2026",
    image: PROJECT_IMAGES[4],
    description: "A comprehensive deep cleaning and sanitization project for Monash University's Clayton Campus rest room facilities. Our team delivered thorough cleaning and sanitization services across multiple buildings, ensuring the highest hygiene standards for students and staff.",
    scope: [
      "Deep cleaning of all rest room facilities",
      "Full sanitization and disinfection",
      "Tile and grout cleaning",
      "Fixture polishing and detailing",
      "Air freshening and odour treatment",
      "Final inspection and quality assurance"
    ],
    size: "University campus facilities",
    projectDetails: {
      location: "Clayton, VIC",
      duration: "2 weeks",
      teamSize: "14 cleaners",
      sqMetres: "3,200 m²"
    },
    outcome: "Restored facilities to as-new condition with measurable improvement in hygiene audit scores. University facilities management reported a 40% reduction in maintenance call-outs following our deep clean program."
  },
  { 
    id: 6,
    title: "56 Kingloch Parade", 
    type: "Builder's Cleaning & Handover", 
    category: "builders", 
    value: "Medium Density Townhouse & Apartment Development", 
    client: "WestUrban", 
    builder: "WestUrban",
    completionDate: "2025",
    image: PROJECT_IMAGES[5],
    description: "A comprehensive builder's cleaning and handover for this medium density townhouse and apartment development in Wantirna. Our team delivered meticulous cleaning across multiple dwellings, ensuring each property was ready for settlement.",
    scope: [
      "Full internal cleaning of all townhouses and apartments",
      "Common area and driveway cleaning",
      "Window cleaning - internal and external",
      "Garage and carport detailing",
      "External facade and balcony cleaning",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Medium density residential development",
    projectDetails: {
      location: "Wantirna, VIC",
      duration: "5 weeks",
      teamSize: "15 cleaners",
      sqMetres: "12,000 m²"
    },
    outcome: "All 24 dwellings achieved settlement-ready status on schedule. Coordinated cleaning across staged handovers, enabling the developer to process settlements efficiently. Zero post-settlement cleaning complaints received."
  },
  { 
    id: 7,
    title: "477 Plummer St", 
    type: "Handover Clean", 
    category: "builders", 
    value: "Commercial Development", 
    client: "LSR Construction", 
    builder: "LSR Construction",
    completionDate: "2026",
    image: PROJECT_IMAGES[6],
    description: "A comprehensive handover cleaning for this commercial development in Port Melbourne. Our team delivered detailed cleaning services ensuring the property was ready for client handover.",
    scope: [
      "Full internal cleaning of all commercial spaces",
      "Window cleaning - internal and external",
      "Floor cleaning and treatment",
      "Common area detailing",
      "Final sparkle cleaning prior to handover"
    ],
    size: "Commercial development",
    projectDetails: {
      location: "Port Melbourne, VIC",
      duration: "2 weeks",
      teamSize: "10 cleaners",
      sqMetres: "5,000 m²"
    },
    outcome: "Delivered on schedule with all areas passing inspection on first review. Client commended the attention to detail and professional service delivery."
  },
];

/* Case Study Modal */
const CaseStudyModal = ({ project, onClose, onNextProject }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm"
    onClick={onClose}
    data-testid="case-study-modal"
  >
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      className="relative bg-white max-w-5xl w-full max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 bg-neutral-900 flex items-center justify-center hover:bg-neutral-800 transition-colors"
        data-testid="modal-close-btn"
      >
        <X className="w-5 h-5 text-white" />
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="aspect-[4/3] md:aspect-auto md:min-h-[600px]">
          <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
        </div>
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {project.type}
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {project.title}
          </h2>

          {/* Project Details */}
          {project.projectDetails && (
            <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-neutral-200">
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Location</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.location}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Duration</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.duration}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Team Size</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.teamSize}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-400 uppercase tracking-wide mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Area</p>
                <p className="text-sm text-neutral-900 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>{project.projectDetails.sqMetres}</p>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-3">Project Overview</h3>
            <p className="text-neutral-600 leading-relaxed text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {project.description}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-3">Scope of Work</h3>
            <ul className="space-y-2">
              {project.scope.map((item) => (
                <li key={item} className="flex items-start gap-2 text-neutral-600 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <span className="w-1.5 h-1.5 bg-[#B8D86B] rounded-full mt-1.5 shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Outcome Section */}
          {project.outcome && (
            <div className="mb-8 p-4 bg-neutral-50 border-l-4 border-[#B8D86B]">
              <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">Outcome</h3>
              <p className="text-neutral-600 leading-relaxed text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {project.outcome}
              </p>
            </div>
          )}

          <button
            onClick={onNextProject}
            data-testid="next-project-btn"
            className="flex items-center gap-2 text-sm font-semibold text-[#B8D86B] hover:text-[#9BC05A] transition-colors self-start"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            View Next Project
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

export default function ProjectsPage() {
  const [selectedProject, setSelectedProject] = useState(null);

  const handleNextProject = () => {
    if (!selectedProject) return;
    const currentIndex = PROJECTS.findIndex(p => p.id === selectedProject.id);
    const nextIndex = (currentIndex + 1) % PROJECTS.length;
    setSelectedProject(PROJECTS[nextIndex]);
  };

  return (
    <div data-testid="projects-page" className="bg-white">
      {/* Hero - Job site photo with overlay */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-28 relative overflow-hidden min-h-[60vh] md:min-h-[50vh]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG}
            alt="" 
            className="w-full h-full object-cover object-[center_40%]"
          />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/50" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.3em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Portfolio
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              A Higher Standard of Cleaning
              <span className="text-[#B8D86B]">.</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              From high-rise developments to commercial fit-outs and large-scale facilities, PKC Property Services delivers consistent, high-standard results across projects of all sizes.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Projects Overview */}
      <section className="py-16 md:py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <ScrollReveal>
              <div>
                <p className="text-neutral-600 leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Our portfolio reflects long-standing partnerships with leading builders, developers and property managers, built on reliability, safety and attention to detail.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 border border-neutral-200">
                  <p className="text-3xl md:text-4xl font-semibold text-[#B8D86B]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <AnimatedCounter target={250} suffix="+" duration={2000} />
                  </p>
                  <p className="text-neutral-500 text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Projects Completed</p>
                </div>
                <div className="bg-white p-6 border border-neutral-200">
                  <p className="text-3xl md:text-4xl font-semibold text-[#B8D86B]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <AnimatedCounter target={50} suffix="+" duration={1800} />
                  </p>
                  <p className="text-neutral-500 text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Active Clients</p>
                </div>
                <div className="bg-white p-6 border border-neutral-200">
                  <p className="text-3xl md:text-4xl font-semibold text-[#B8D86B]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <AnimatedCounter target={4} suffix="+" duration={1500} />
                  </p>
                  <p className="text-neutral-500 text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Years Experience</p>
                </div>
                <div className="bg-white p-6 border border-neutral-200">
                  <p className="text-3xl md:text-4xl font-semibold text-[#B8D86B]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <AnimatedCounter target={85} suffix="%" duration={1800} />
                  </p>
                  <p className="text-neutral-500 text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Repeat Client Engagements</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {PROJECTS.map((project, idx) => (
              <ScrollReveal key={project.id} delay={idx * 0.05}>
                <div
                  className="group relative aspect-[4/3] overflow-hidden bg-neutral-100"
                  data-testid={`project-card-${idx}`}
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <p className="text-[#B8D86B] text-sm font-bold tracking-wide uppercase mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {project.type}
                    </p>
                    <h3 className="text-white text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {project.title}
                    </h3>
                    <p className="text-white text-sm font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {project.client}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ready to get started?
            </h2>
            <p className="text-neutral-600 text-lg mb-10 max-w-lg mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Speak with our team about tailored commercial cleaning solutions.
            </p>
            <Link
              to="/contact"
              data-testid="projects-cta-btn"
              className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-10 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* Case Study Modal */}
      <AnimatePresence>
        {selectedProject && (
          <CaseStudyModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
            onNextProject={handleNextProject}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
